/**
 * Kriegsakademie: the instructor's card and the fourteen lessons (tutorial/steps.ts holds the
 * conditions, keyed by lesson id). Body paragraphs are separated by a blank line; `[Key]` renders
 * as a keycap, `*text*` as emphasis.
 */
export const academy = {
  'ka.head': 'KRIEGSAKADEMIE',
  'ka.lesson': 'LESSON {n} OF {total}',
  'ka.end': 'End the Kriegsakademie (the match continues)',
  'ka.back': 'Back',
  'ka.showMe': 'Show me',
  'ka.graduate': 'Graduate',
  'ka.next': 'Next',
  'ka.skip': 'Skip',
  'ka.skip.hint': 'Skip this lesson',
  'ka.done': 'Erledigt',
  'ka.cert.head': 'KRIEGSAKADEMIE · CERTIFICATE',
  'ka.cert.title': 'The course is complete.',
  'ka.cert.text': 'The staff college releases you to the front. The war is yours to win.',
  'ka.cert.stamp': 'Graduated',

  'tut.camera.title': 'The map table',
  'tut.camera.body':
    'Welcome to the Kriegsakademie, Leutnant. Before you command a single man, learn to read the table.\n\nDrag with the *right* mouse button — or hold the arrow keys — to pan. The wheel zooms. The situation map in the corner shows the whole theatre; click it to jump.',
  'tut.camera.task': 'Pan and zoom the map.',

  'tut.select.title': 'Your divisions',
  'tut.select.body':
    'The red counters are ours. Each is a division, and the picture on it names its arm — riflemen, lorried infantry, tanks, guns, cavalry, the staff car of a headquarters. (Settings can print the staff’s NATO symbols instead.)\n\nLeft-click one of our divisions. Dragging a box selects several.',
  'tut.select.task': 'Select a division.',

  'tut.card.title': 'The unit card',
  'tut.card.body':
    'The card on the right is all the staff knows of the division. *Strength* is men and guns — slow to lose, slow to replace. *Organisation* is cohesion: it melts in battle and returns at rest; at zero the division breaks.\n\n*Supply* reaches it from our cities along the roads; without it nothing recovers. *Entrenched* grows while it stands still — dug-in troops are hard to shift.',

  'tut.hq.title': 'Headquarters',
  'tut.hq.body':
    'You never move a division yourself. You write orders, and they leave from the nearest headquarters — the HQ counter behind our line.\n\nSelect it: the dashed line around it marks its *command radius*. Inside, orders go by field telephone and arrive within the hour. Beyond it a mounted courier must ride the roads.',
  'tut.hq.task': 'Select the headquarters.',

  'tut.order.title': 'Write an order',
  'tut.order.body':
    'Select a division — one beyond the dashed line, if you like — press [M] for Move and click a hex behind our line. A right-click on the map does the same.\n\nThe clock is stopped, but the order leaves at once: down the telephone line, or with a courier on the road. Couriers can be captured — and then the enemy reads your plan.',
  'tut.order.task': 'Send a Move order.',

  'tut.clock.title': 'Start the clock',
  'tut.clock.body':
    'Now, time. Press [Space] or a speed button to start the war — speed I is slow enough to watch.\n\nFollow the order: a dashed arrow while it is on its way — along the wire, or with a rider on the road. When it arrives the teletype prints *acknowledges orders* and the hours it took. That delay is friction.',
  'tut.clock.task': 'Resume the clock and wait for an acknowledgement.',

  'tut.intel.title': 'Reading the enemy',
  'tut.intel.body':
    'The blue counters are the enemy — as far as we know. A “?” is a mere *contact*; *identified* gives type and designation; *assessed*, when we stand adjacent, adds strength and unmasks decoys.\n\nEnemies we lose sight of become *ghosts* marked with the age of the report. Old reports lie. [I] opens the intelligence file.',
  'tut.intel.task': 'Click an enemy counter to inspect it.',

  'tut.attack.title': 'The assault',
  'tut.attack.body':
    'Select divisions next to the enemy and right-click his counter — or press [A] and click. Attack from several sides: every extra direction adds 15 %. Artillery within two hexes joins in.\n\nA battle marker appears on the contested hex; its bar shows who is winning. The loser bleeds organisation first.',
  'tut.attack.task': 'Order an attack.',

  'tut.hhour.title': 'H-hour',
  'tut.hhour.body':
    'Great offensives jump off together. Drag a box around several divisions, set H-hour to [+6h], then give Move or Attack.\n\nEach division waits — *Awaiting H-hour* — until the appointed time. A courier who arrives late means a late start.',
  'tut.hhour.task': 'Give one H-hour order to two or more divisions.',

  'tut.posture.title': 'Auftragstaktik',
  'tut.posture.body':
    'Orders will be late; the war will not wait. A *posture* tells a division what to do meanwhile: *Aggressive* counter-attacks weak neighbours on its own, *Balanced* holds, *Defensive* falls back before it is shattered.\n\nA change of posture travels like any other order.',
  'tut.posture.task': 'Change the posture of a division.',

  'tut.production.title': 'Production',
  'tut.production.body':
    'Cities in supply yield industry and manpower. Press [P] for the production office: choose where to deploy and order a new formation. Four are built in parallel.',
  'tut.production.task': 'Order a new division.',

  'tut.research.title': 'Research',
  'tut.research.body':
    'Press [T]. Mind the signals branch: *Field Radio* reaches any unit within the hour — but enemy cryptanalysts may read it. The *Cryptanalysis Bureau* reads theirs; the *Rotor Cipher* protects ours.',
  'tut.research.task': 'Choose a research project.',

  'tut.supply.title': 'Supply & the Kessel',
  'tut.supply.body':
    'Supply flows from our cities through our territory, further along roads and railways. Point at a hex: the strip at the bottom shows its supply.\n\nCut the enemy’s lines and his divisions starve; close the ring and the teletype prints *KESSEL*. Guard your own flanks — a division cut off for two days surrenders.',

  'tut.victory.title': 'Victory',
  'tut.victory.body':
    'Cities are worth victory points — the bar at the top. Hold 60 % of all points for 24 hours and the war is won. A nation that loses its capital and most of its cities capitulates.\n\nThat is all the Akademie can teach. The enemy opposite is a recruit, and the war goes on. Good luck, Herr General.',
} as const;
